import { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const IDLE_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds
const WARNING_BEFORE = 60 * 1000; // Show warning 1 minute before logout

export default function useAutoLogout() {
  const navigate = useNavigate();
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const countdownRef = useRef(null);
  const isInitialized = useRef(false);
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(60);

  const logout = useCallback(() => {
    // Clear all timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    
    // Clear auth data from localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    setShowWarning(false);
    toast.error('Sesi berakhir karena tidak ada aktivitas', { duration: 4000 });
    navigate('/login');
  }, [navigate]);

  const resetTimer = useCallback(() => {
    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    
    // Hide warning if shown
    setShowWarning(false);
    setCountdown(60);

    // Only set timers if user is logged in
    const token = localStorage.getItem('token');
    if (!token) return;

    // Set warning timer (4 minutes)
    warningRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(60);
      
      // Start countdown
      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      toast('⚠️ Anda akan logout dalam 1 menit karena tidak ada aktivitas', {
        duration: 5000,
        icon: '⏰'
      });
    }, IDLE_TIMEOUT - WARNING_BEFORE);

    // Set logout timer (5 minutes)
    timeoutRef.current = setTimeout(logout, IDLE_TIMEOUT);
  }, [logout]);

  const stayLoggedIn = useCallback(() => {
    setShowWarning(false);
    resetTimer();
    toast.success('Sesi diperpanjang');
  }, [resetTimer]);

  useEffect(() => {
    // Events to track user activity
    const events = [
      'mousedown',
      'mousemove', 
      'keydown',
      'scroll',
      'touchstart',
      'click'
    ];

    // Throttle reset to avoid too many calls
    let lastReset = Date.now();
    const throttledReset = () => {
      const now = Date.now();
      if (now - lastReset > 1000) { // Only reset every 1 second max
        lastReset = now;
        resetTimer();
      }
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, throttledReset, { passive: true });
    });

    // Initial timer - use setTimeout to avoid setState in effect body
    if (!isInitialized.current) {
      isInitialized.current = true;
      setTimeout(() => {
        resetTimer();
      }, 0);
    }

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledReset);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [resetTimer]);

  return { showWarning, countdown, stayLoggedIn, logout };
}
